import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, ExternalLink, FlaskConical, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { FieldSelectFit, TRADE_LOG_FILTER_INPUT_CLASS } from "@/components/trade-filter-selects";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PinIcon, type PinIconHandle } from "@/components/ui/pin-icon";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfirm } from "@/components/ui/use-confirm";
import { cn } from "@/lib/utils";
import { getStrategyRdResultCard } from "../../../services/strategyRdApi";
import {
  bulkStrategyRdLibrary,
  groupLibrary,
  listStrategyRdLibrary,
  type StrategyRdLibraryAction,
  type StrategyRdLibraryFamily,
  type StrategyRdLibraryItem,
} from "../../../services/strategyRdLibraryApi";
import type { StrategyRdCoachResultCard } from "../../../types/strategyRdCoach";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { formatStrategyRdPct, formatStrategyRdWinRate } from "../../../utils/strategyRd";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { StrategyRdEquityArea } from "./coach/StrategyRdCoachLabActionCard";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import { STRATEGY_PINE_BADGE_CLASS } from "../Strategies/strategyTablePineBadges";

/** Saved backtests: 100 at most — delete old ones to make room. */
export const STRATEGY_RD_LIBRARY_LIMIT = 100;

type Filter = "all" | "saved" | "unsaved";
type Sort = "newest" | "oldest" | "name";

const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name A–Z" },
];

const FAMILY_SAVED = (f: StrategyRdLibraryFamily) => f.items.some((x) => x.strategy_id);

function matches(fam: StrategyRdLibraryFamily, q: string, filter: Filter): boolean {
  if (filter === "saved" && !FAMILY_SAVED(fam)) return false;
  if (filter === "unsaved" && FAMILY_SAVED(fam)) return false;
  if (!q) return true;
  return fam.items.some((it) => `${it.name} ${it.symbol ?? ""} ${it.candle_type ?? ""}`.toLowerCase().includes(q));
}

// --- Previews: each backtest's headline numbers + account curve, loaded when its card shows ---------

const previewCache = new Map<string, Promise<StrategyRdCoachResultCard | null>>();

function loadPreview(id: string): Promise<StrategyRdCoachResultCard | null> {
  let p = previewCache.get(id);
  if (!p) {
    p = getStrategyRdResultCard(id).catch(() => null);
    previewCache.set(id, p);
  }
  return p;
}

/** The backtest's preview once `active` (on screen); null while loading or when it never ran. */
function usePreview(id: string, active: boolean): { card: StrategyRdCoachResultCard | null; loading: boolean } {
  const [card, setCard] = useState<StrategyRdCoachResultCard | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!active) return;
    let live = true;
    setLoading(true);
    void loadPreview(id).then((c) => {
      if (!live) return;
      setCard(c);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [active, id]);
  return { card, loading };
}

/** True once the element has scrolled into view (and stays true). */
function useSeen<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

// --- Small pieces ----------------------------------------------------------------------------------

const POS = "text-[color:var(--chart-positive,#10b981)]";
const NEG = "text-[color:var(--chart-negative,#ef4444)]";
const tone = (v: number | null | undefined) => (v == null ? "text-[#4B5563]" : v > 0 ? POS : v < 0 ? NEG : "text-[#4B5563]");

function money(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "N/A";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function signedPct(v: number | null | undefined): string {
  const t = formatStrategyRdPct(v);
  return t == null ? "N/A" : v != null && v > 0 ? `+${t}` : t;
}

const BADGE_EDGE = "!border !border-solid";

function ActiveBadge({ label = "Active" }: { label?: string }): JSX.Element {
  return (
    <Badge
      variant="pine-compose-active"
      className={cn(STRATEGY_PINE_BADGE_CLASS, "gap-1")}
      title="Open in the Lab: its results are under the chart"
      data-strategy-rd-library-active=""
    >
      <span className="size-1.5 rounded-full bg-white" aria-hidden />
      {label}
    </Badge>
  );
}

function SavedBadge(): JSX.Element {
  return (
    <Badge
      variant="pine-status-ready"
      className={cn(STRATEGY_PINE_BADGE_CLASS, BADGE_EDGE, "!border-[color:var(--tessera-pnl-positive-border)]")}
      title="Saved as a strategy"
    >
      Saved as strategy
    </Badge>
  );
}

const VERDICT: Record<string, { label: string; tone: "good" | "bad" | "neutral" }> = {
  held_up: { label: "Held up on unseen", tone: "good" },
  weaker: { label: "Weaker on unseen", tone: "neutral" },
  failed: { label: "Failed on unseen", tone: "bad" },
  too_few_trades: { label: "Too few trades", tone: "neutral" },
};
const VERDICT_TONE = {
  good: "bg-[#06E363]/12 text-[#049a44] before:bg-[#16a34a]",
  bad: "bg-[#F70009]/10 text-[#c8000a] before:bg-[#dc2626]",
  neutral: "bg-muted text-[#6B7280] before:bg-[#9CA3AF]",
} as const;

function VerdictBadge({ verdict, short = false }: { verdict: string | null | undefined; short?: boolean }): JSX.Element | null {
  if (!verdict) return null;
  const v = VERDICT[verdict] ?? { label: verdict.replace(/_/g, " "), tone: "neutral" as const };
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[6px] px-1.5 font-[family-name:var(--font-primary)] text-[11px]",
        "before:size-1.5 before:rounded-full before:content-['']",
        VERDICT_TONE[v.tone]
      )}
      title="The last 30% of the window, held out from tuning"
    >
      {short ? v.label.replace(/ on unseen$/, "") : v.label}
    </span>
  );
}

/** A tiny account curve for version chips and rows: green ended up, red ended down. */
function MiniCurve({ points, className }: { points: ReadonlyArray<{ equity: number }>; className?: string }): JSX.Element {
  if (points.length < 2) return <span className={cn("block", className)} />;
  const w = 100;
  const h = 30;
  const ys = points.map((p) => p.equity);
  const min = Math.min(...ys);
  const span = Math.max(...ys) - min || 1;
  const d = points
    .map((p, i) => `${i ? "L" : "M"}${((i / (points.length - 1)) * w).toFixed(1)},${(2 + (1 - (p.equity - min) / span) * (h - 4)).toFixed(1)}`)
    .join(" ");
  const up = ys[ys.length - 1] >= ys[0];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cn("block", className)} aria-hidden>
      <path d={d} fill="none" stroke={up ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <p className="font-[family-name:var(--font-primary)] text-[11px] text-[#9CA3AF]">{label}</p>
      <div className="font-[family-name:var(--font-secondary)] text-sm font-medium tabular-nums">{children}</div>
    </div>
  );
}

function PinButton({ pinned, busy, name, onToggle }: { pinned: boolean; busy: boolean; name: string; onToggle: () => void }) {
  const ref = useRef<PinIconHandle | null>(null);
  return (
    <Button
      type="button"
      variant={pinned ? "outline-brand" : "outline"}
      size="sm"
      data-font="primary"
      className="size-8 shrink-0 p-0"
      disabled={busy}
      aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
      aria-pressed={pinned}
      title={pinned ? "Unpin" : "Pin to the top"}
      onPointerDownCapture={() => void ref.current?.startAnimation()}
      onMouseEnter={() => void ref.current?.startAnimation()}
      onMouseLeave={() => void ref.current?.stopAnimation()}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      data-strategy-rd-library-pin=""
    >
      <PinIcon ref={ref} size={15} filled={pinned} className="shrink-0 text-current" aria-hidden />
    </Button>
  );
}

function itemWhen(it: StrategyRdLibraryItem, timeZone: string | undefined): string | null {
  const when = it.updated_at ?? it.created_at;
  return when ? formatTradeDateTimeForDisplay(when, timeZone) : null;
}

type ItemActions = {
  busy: boolean;
  currentId: string | null;
  timeZone?: string;
  onOpen: (id: string) => void;
  onSaveAsStrategy?: (id: string) => void;
  onOpenStrategy: (strategyId: string) => void;
  onAction: (action: StrategyRdLibraryAction, ids: string[]) => void;
};

function OpenAndSave({ item, a }: { item: StrategyRdLibraryItem; a: ItemActions }): JSX.Element {
  return (
    <>
      <Button
        type="button"
        variant="outline-brand"
        size="sm"
        data-font="primary"
        className="h-8 gap-1.5 px-3"
        onClick={() => a.onOpen(item.id)}
      >
        <ExternalLink className="size-3.5" aria-hidden />
        <span className="normal-case tracking-normal">{item.id === a.currentId ? "Open now" : "Open"}</span>
      </Button>
      {item.strategy_id ? (
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
          className="h-8 gap-1.5 px-3"
          onClick={() => a.onOpenStrategy(item.strategy_id!)}
        >
          <FlaskConical className="size-3.5" aria-hidden />
          <span className="normal-case tracking-normal">Open strategy</span>
        </Button>
      ) : a.onSaveAsStrategy ? (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 px-3"
          disabled={a.busy}
          title="Turn this backtest into a Strategy. Its rules become version 1"
          onClick={() => a.onSaveAsStrategy!(item.id)}
          data-strategy-rd-library-save-strategy=""
        >
          <span className="normal-case tracking-normal">Save as strategy</span>
        </Button>
      ) : null}
    </>
  );
}

// --- One idea as a card -------------------------------------------------------------------------

function VersionChip({
  item,
  current,
  onOpen,
}: {
  item: StrategyRdLibraryItem;
  current: boolean;
  onOpen: () => void;
}): JSX.Element {
  const { card, loading } = usePreview(item.id, true);
  const ret = card?.stats.return_on_starting_equity_pct ?? null;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Open v${item.version}`}
      className={cn(
        "flex min-w-0 flex-1 flex-col gap-0.5 !rounded-[6px] !border !border-solid bg-background !px-1.5 !py-1 text-left transition-colors",
        current ? "!border-[color:var(--brand-tropical-surf)]" : "!border-border hover:!border-[color:var(--brand-tropical-surf)]/50"
      )}
    >
      <span className="flex items-center justify-between gap-1">
        <span className={cn("font-[family-name:var(--font-primary)] text-[11px] font-medium", current ? "text-[color:var(--brand-tropical-surf)]" : "text-[#4B5563]")}>
          v{item.version}
        </span>
        <span className={cn("font-[family-name:var(--font-secondary)] text-[10px] tabular-nums", tone(ret))}>{signedPct(ret)}</span>
      </span>
      <MiniCurve points={card?.equity_curve ?? []} className="h-[18px] w-full" />
    </button>
  );
}

function IdeaCard({
  family,
  a,
  onCompare,
}: {
  family: StrategyRdLibraryFamily;
  a: ItemActions;
  onCompare: () => void;
}): JSX.Element {
  const [ref, seen] = useSeen<HTMLDivElement>();
  const lead = family.items[0];
  const { card, loading } = usePreview(lead.id, seen);
  const s = card?.stats;
  const many = family.items.length > 1;
  const activeItem = family.items.find((x) => x.id === a.currentId) ?? null;
  const saved = family.items.some((x) => x.strategy_id);
  const net = card?.net_pnl ?? s?.sum_pnl ?? null;
  const ids = family.items.map((x) => x.id);
  const pf = s?.profit_factor ?? s?.profit_factor_r ?? null;
  return (
    <div
      ref={ref}
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-[8px] !border !border-solid bg-background",
        activeItem ? "!border-[color:var(--brand-tropical-surf)] bg-[color:var(--brand-tropical-surf)]/[0.03]" : "!border-border"
      )}
      data-strategy-rd-library-family={family.root_id}
      data-active={activeItem ? "" : undefined}
    >
      <div className="flex items-start gap-2 px-4 pb-2 pt-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => a.onOpen(lead.id)}
              className="min-w-0 truncate text-left font-[family-name:var(--font-primary)] !text-[15px] font-medium !text-[color:var(--brand-tropical-surf)] hover:underline"
              title="Open the latest version"
            >
              {family.name}
            </button>
            {activeItem ? <ActiveBadge label={activeItem.id === lead.id || !many ? "Active" : `Active · v${activeItem.version}`} /> : null}
            {saved ? <SavedBadge /> : null}
          </div>
          <p className={cn(strategyRdMetaClassName, "mt-0.5 font-[family-name:var(--font-secondary)] text-[11px]")}>
            {[[lead.symbol, lead.candle_type].filter(Boolean).join(" · ") || null, itemWhen(lead, a.timeZone)].filter(Boolean).join(" · ")}
          </p>
        </div>
        <PinButton pinned={family.pinned} busy={a.busy} name={family.name} onToggle={() => a.onAction(family.pinned ? "unpin" : "pin", [lead.id])} />
        <TableOutlineDeleteIconButton
          marker="strategyRd"
          className="size-8"
          aria-label={`Delete ${family.name}`}
          disabled={a.busy}
          onClick={() => a.onAction("delete", ids)}
        />
      </div>

      <div className="px-3">
        {card && card.equity_curve.length > 1 ? (
          <StrategyRdEquityArea points={card.equity_curve} height={76} />
        ) : (
          <div className="flex h-[76px] items-center justify-center rounded-[6px] bg-muted/40" data-strategy-rd-library-preview-empty="">
            {loading || !seen ? (
              <Loader2 className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
            ) : (
              <span className={cn(strategyRdMetaClassName, "text-xs")}>{lead.trades == null ? "Not run yet" : "No curve for this run"}</span>
            )}
          </div>
        )}
      </div>

      {loading || !seen ? (
        // Loading: placeholders, not N/A or the raw trade count that the stats then replace.
        <div className="grid grid-cols-4 gap-2 px-4 pb-2 pt-2.5" data-strategy-rd-library-stats-loading="">
          {["Net P&L", "Win rate", "Max DD", "Trades"].map((label) => (
            <Stat key={label} label={label}>
              <Skeleton className="mt-1 h-4 w-14 rounded-[4px]" />
            </Stat>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-4 gap-2 px-4 pb-2 pt-2.5">
        <Stat label="Net P&L">
          <span className={cn("whitespace-nowrap", tone(net))}>{money(net)}</span>
          <span className={cn("block text-[10.5px] font-normal", tone(s?.return_on_starting_equity_pct))}>
            {signedPct(s?.return_on_starting_equity_pct)} return
          </span>
        </Stat>
        <Stat label="Win rate">
          <span className="text-[#4B5563]">{formatStrategyRdWinRate(s?.win_rate) ?? "N/A"}</span>
        </Stat>
        <Stat label="Max DD">
          <span className={s?.max_drawdown_pct ? NEG : "text-[#4B5563]"}>
            {s?.max_drawdown_pct != null ? formatStrategyRdPct(-Math.abs(s.max_drawdown_pct)) : "N/A"}
          </span>
        </Stat>
        <Stat label="Trades">
          <span className="text-[#4B5563]">{s?.closed_count ?? lead.trades ?? "N/A"}</span>
        </Stat>
      </div>
      )}
      <div className="flex min-h-5 items-center gap-2 px-4 pb-3">
        <VerdictBadge verdict={card?.split?.verdict} />
        {pf != null ? (
          <span className="font-[family-name:var(--font-secondary)] text-[11px] text-[#9CA3AF]">Profit factor {pf.toFixed(2)}</span>
        ) : null}
      </div>

      {many ? (
        <div className="flex flex-col gap-1.5 !border-0 !border-t !border-solid !border-border bg-muted/40 px-4 py-2.5">
          <div className="flex items-center justify-between">
            <span className="font-[family-name:var(--font-primary)] text-[11px] font-medium text-[#6B7280]">
              {family.items.length} versions
            </span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              data-font="primary"
              className="h-6 gap-1 px-1.5 !text-xs !text-[color:var(--brand-tropical-surf)] [&>span]:!normal-case [&>span]:!tracking-normal"
              onClick={onCompare}
              data-strategy-rd-library-compare=""
            >
              <span>Compare versions</span>
              <ChevronDown className="size-3.5 -rotate-90" aria-hidden />
            </Button>
          </div>
          {seen ? (
            <div className="flex gap-1.5">
              {[...family.items]
                .sort((x, y) => x.version - y.version)
                .slice(-6)
                .map((it) => (
                  <VersionChip key={it.id} item={it} current={it.id === (activeItem?.id ?? lead.id)} onOpen={() => a.onOpen(it.id)} />
                ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-auto flex items-center justify-end gap-2 !border-0 !border-t !border-solid !border-border px-4 py-2.5">
        <OpenAndSave item={activeItem ?? lead} a={a} />
      </div>
    </div>
  );
}

// --- One idea's versions, compared ------------------------------------------------------------------

type VersionTableRow = {
  item: StrategyRdLibraryItem;
  card: StrategyRdCoachResultCard | null;
  loading: boolean;
  latest: boolean;
};

/** Every version's result card (shared cache), for the compare table. */
function useVersionCards(ids: string[]): Record<string, StrategyRdCoachResultCard | null | undefined> {
  const [cards, setCards] = useState<Record<string, StrategyRdCoachResultCard | null | undefined>>({});
  const key = ids.join(",");
  useEffect(() => {
    let live = true;
    for (const id of key.split(",").filter(Boolean)) {
      void loadPreview(id).then((c) => {
        if (live) setCards((prev) => ({ ...prev, [id]: c }));
      });
    }
    return () => {
      live = false;
    };
  }, [key]);
  return cards;
}

/** One idea's versions side by side, in the app's data table (as Trades and the Trade Log). */
function CompareView({ family, a }: { family: StrategyRdLibraryFamily; a: ItemActions }): JSX.Element {
  const items = useMemo(() => [...family.items].sort((x, y) => y.version - x.version), [family.items]);
  const cards = useVersionCards(items.map((it) => it.id));
  const rows: VersionTableRow[] = items.map((item, i) => ({
    item,
    card: cards[item.id] ?? null,
    loading: cards[item.id] === undefined,
    latest: i === 0,
  }));
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const wait = <Skeleton className="h-4 w-12 rounded-[4px]" />;
  const columns: ColumnDef<VersionTableRow, unknown>[] = [
    {
      id: "version",
      accessorFn: (r) => r.item.version,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Version" />,
      cell: ({ row }) => {
        const { item, latest } = row.original;
        return (
          <div className="flex min-w-[10rem] max-w-[16rem] flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-1.5 font-[family-name:var(--font-primary)] text-[13px]">
              <span className="font-medium text-[color:var(--brand-tropical-surf)]">v{item.version}</span>
              <span className="truncate text-gray-500 dark:text-white">{item.name}</span>
              {latest ? (
                <Badge variant="pine-meta" className={cn(STRATEGY_PINE_BADGE_CLASS, BADGE_EDGE, "!border-border")}>
                  Latest
                </Badge>
              ) : null}
              {item.id === a.currentId ? <ActiveBadge /> : null}
              {item.strategy_id ? <SavedBadge /> : null}
              {/* How it did on unseen data, with the version's other badges. */}
              <VerdictBadge verdict={row.original.card?.split?.verdict} short />
            </span>
            <span className={cn(strategyRdMetaClassName, "font-[family-name:var(--font-secondary)] text-[11px]")}>
              {[[item.symbol, item.candle_type].filter(Boolean).join(" · ") || null, itemWhen(item, a.timeZone)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        );
      },
    },
    {
      id: "curve",
      header: "Account curve",
      enableSorting: false,
      cell: ({ row }) => <MiniCurve points={row.original.card?.equity_curve ?? []} className="h-8 w-[88px]" />,
    },
    {
      id: "net",
      accessorFn: (r) => r.card?.net_pnl ?? r.card?.stats?.sum_pnl ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Net P&L" />,
      cell: ({ row }) => {
        const { card, loading } = row.original;
        const net = card?.net_pnl ?? card?.stats?.sum_pnl ?? null;
        return loading ? wait : (
          <TradeLogSortableCell className="font-medium">
            <span className="flex flex-col">
              <span className={tone(net)}>{money(net)}</span>
              <span className={cn("text-[10.5px] font-normal", tone(card?.stats?.return_on_starting_equity_pct))}>
                {signedPct(card?.stats?.return_on_starting_equity_pct)}
              </span>
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "winRate",
      accessorFn: (r) => r.card?.stats?.win_rate ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Win rate" />,
      cell: ({ row }) =>
        row.original.loading ? wait : (
          <TradeLogSortableCell className="font-medium">{formatStrategyRdWinRate(row.original.card?.stats?.win_rate) ?? "N/A"}</TradeLogSortableCell>
        ),
    },
    {
      id: "maxDd",
      accessorFn: (r) => r.card?.stats?.max_drawdown_pct ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Max DD" />,
      cell: ({ row }) => {
        const dd = row.original.card?.stats?.max_drawdown_pct;
        return row.original.loading ? wait : (
          <TradeLogSortableCell className="font-medium">
            <span className={dd ? NEG : undefined}>{dd != null ? formatStrategyRdPct(-Math.abs(dd)) : "N/A"}</span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "trades",
      accessorFn: (r) => r.card?.stats?.closed_count ?? r.item.trades ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Trades" />,
      cell: ({ row }) =>
        row.original.loading ? wait : (
          <TradeLogSortableCell className="font-medium">
            {row.original.card?.stats?.closed_count ?? row.original.item.trades ?? "N/A"}
          </TradeLogSortableCell>
        ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const { item } = row.original;
        return (
          <div className="flex items-center justify-end gap-2">
            <OpenAndSave item={item} a={a} />
            <TableOutlineDeleteIconButton
              marker="strategyRd"
              className="size-8"
              aria-label={`Delete v${item.version}`}
              disabled={a.busy}
              onClick={() => a.onAction("delete", [item.id])}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div data-strategy-rd-library-versions="">
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(r) => r.item.id}
        activeRowId={a.currentId ?? undefined}
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination={rows.length > 20}
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No versions."
      />
    </div>
  );
}

// --- The dialog --------------------------------------------------------------------------------------

/**
 * Backtest history: every backtest, grouped by idea — the latest run of each as a card (account
 * curve, headline numbers, the unseen-data check, its versions), one click to open it or save it
 * as a strategy. "Compare versions" lays one idea's runs side by side.
 */
export function StrategyRdLibraryDialog({
  open,
  onOpenChange,
  currentId,
  timeZone,
  onOpen,
  onDeleted,
  onSaveAsStrategy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentId: string | null;
  timeZone?: string;
  onOpen: (id: string) => void;
  onDeleted: (id: string) => void;
  /** Promote one backtest to a Strategy (opens it in the Lab, then the save dialog). */
  onSaveAsStrategy?: (id: string) => void;
}): JSX.Element {
  const history = useHistory();
  const [items, setItems] = useState<StrategyRdLibraryItem[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("newest");
  const [query, setQuery] = useState("");
  const [compareRoot, setCompareRoot] = useState<string | null>(null);
  const [busy, setBusy] = useState<StrategyRdLibraryAction | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setItems(null);
    listStrategyRdLibrary()
      .then(setItems)
      .catch((err) => {
        presentMutationError(err, "Could not load your backtests");
        setItems([]);
      });
  }, []);
  useEffect(() => {
    if (!open) return;
    setCompareRoot(null);
    load();
  }, [load, open]);

  const allFamilies = useMemo(() => groupLibrary(items ?? []), [items]);
  const q = query.trim().toLowerCase();
  const families = useMemo(() => {
    const list = allFamilies.filter((f) => matches(f, q, filter));
    if (sort === "name") return [...list].sort((x, y) => x.name.localeCompare(y.name));
    if (sort === "oldest") return [...list].reverse();
    return list; // groupLibrary: pinned first, then newest
  }, [allFamilies, filter, q, sort]);
  const savedCount = allFamilies.filter(FAMILY_SAVED).length;
  const compareFamily = compareRoot ? allFamilies.find((f) => f.root_id === compareRoot) ?? null : null;

  const run = async (action: StrategyRdLibraryAction, ids: string[]) => {
    if (!ids.length) return;
    if (
      action === "delete" &&
      !(await confirm({
        title: ids.length === 1 ? "Delete this backtest?" : `Delete ${ids.length} backtests?`,
        description: "Their trades are deleted too. This can't be undone.",
        confirmLabel: "Delete",
      }))
    )
      return;
    setBusy(action);
    try {
      const res = await bulkStrategyRdLibrary(ids, action);
      const hit = new Set(action === "delete" ? res.ids : ids);
      setItems((prev) => {
        if (!prev) return prev;
        if (action === "delete") return prev.filter((x) => !hit.has(x.id));
        return prev.map((x) => (hit.has(x.id) ? { ...x, pinned: action === "pin" } : x));
      });
      if (action === "delete") hit.forEach((id) => onDeleted(id));
      const n = action === "delete" ? res.ids.length : ids.length;
      const noun = `${n} backtest${n === 1 ? "" : "s"}`;
      toast.success(`${noun} ${{ pin: "pinned", unpin: "unpinned", delete: "deleted" }[action]}`);
    } catch (err) {
      presentMutationError(err, "Could not update the backtests");
    } finally {
      setBusy(null);
    }
  };

  const actions: ItemActions = {
    busy: Boolean(busy),
    currentId,
    timeZone,
    onOpen: (id) => {
      onOpen(id);
      onOpenChange(false);
    },
    onSaveAsStrategy: onSaveAsStrategy
      ? (id) => {
          onOpenChange(false);
          onSaveAsStrategy(id);
        }
      : undefined,
    onOpenStrategy: (strategyId) => {
      onOpenChange(false);
      history.push(`/strategies/${encodeURIComponent(strategyId)}`);
    },
    onAction: (action, ids) => void run(action, ids),
  };

  // The open backtest's card scrolls into view when the dialog opens.
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      scrolledRef.current = false;
      return;
    }
    if (!items || !currentId || scrolledRef.current) return;
    const fam = allFamilies.find((f) => f.items.some((x) => x.id === currentId));
    if (!fam) return;
    scrolledRef.current = true;
    window.setTimeout(() => {
      document.querySelector(`[data-strategy-rd-library-family="${fam.root_id}"]`)?.scrollIntoView({ block: "nearest" });
    }, 60);
  }, [allFamilies, currentId, items, open]);

  const backtestCount = items?.length ?? 0;

  return (
    <>
      <StrategyPineDetailDialog
        open={open}
        onOpenChange={onOpenChange}
        title={compareFamily ? `${compareFamily.name} · ${compareFamily.items.length} versions` : "Backtest history"}
        description={
          compareFamily
            ? "Each run of this idea, newest first. Open any version, or save the one you want to keep as a strategy."
            : `Every backtest you've run, grouped by idea. The latest run on each card. Save the ones worth keeping as strategies.${
                items ? ` ${backtestCount} of ${STRATEGY_RD_LIBRARY_LIMIT} backtests.` : ""
              }`
        }
        contentClassName={compareFamily ? "sm:max-w-6xl" : "sm:max-w-5xl"}
        bodyClassName="flex flex-col gap-3 overflow-x-hidden"
        contentProps={{ "data-strategy-rd-library": "" }}
        dismissLabel="Close"
      >
        {compareFamily ? (
          <>
            <div>
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                data-font="primary"
                className="h-8 gap-1.5 px-3 shadow-none"
                onClick={() => setCompareRoot(null)}
                data-strategy-rd-library-back=""
              >
                <ArrowLeft className="size-3.5" aria-hidden />
                <span className="normal-case tracking-normal">All ideas</span>
              </Button>
            </div>
            <CompareView family={compareFamily} a={actions} />
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Tabs data-trade-log-tabs="" value={filter} onValueChange={(v) => setFilter(v as Filter)} className="w-fit shrink-0">
                <div className="overflow-x-auto overflow-y-hidden">
                  <TabsList className="min-w-max">
                    <TabsTrigger value="all">All · {allFamilies.length}</TabsTrigger>
                    <TabsTrigger value="saved">Saved as strategy · {savedCount}</TabsTrigger>
                    <TabsTrigger value="unsaved">Not saved · {allFamilies.length - savedCount}</TabsTrigger>
                  </TabsList>
                </div>
              </Tabs>
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search backtests…"
                autoComplete="off"
                aria-label="Search backtests"
                data-font="primary"
                data-strategy-rd-library-search=""
                // Same height as the tabs beside it — set in globals.css (the base input rule pins h-8).
                className={cn(TRADE_LOG_FILTER_INPUT_CLASS, "min-w-[12rem] flex-1 normal-case tracking-normal")}
              />
              <FieldSelectFit
                id="strategy-rd-library-sort"
                value={sort}
                onValueChange={(v) => setSort(v as Sort)}
                options={SORTS}
                placeholder="Sort"
              />
            </div>

            {items == null ? (
              <p className={cn(strategyRdMetaClassName, "flex items-center gap-2 py-6")}>
                <Loader2 className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
                Loading…
              </p>
            ) : families.length === 0 ? (
              <p className={cn(strategyRdMetaClassName, "py-6 text-center")}>
                {q || filter !== "all" ? "Nothing matches." : "No backtests yet: ask TES for a strategy, or run one in the Lab."}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-strategy-rd-library-cards="">
                {families.map((fam) => (
                  <IdeaCard key={fam.root_id} family={fam} a={actions} onCompare={() => setCompareRoot(fam.root_id)} />
                ))}
              </div>
            )}
          </>
        )}
      </StrategyPineDetailDialog>
      {confirmDialog}
    </>
  );
}
